import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { InteractiveElement, contextMenuRules, inlineTextIconMargin, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ClickAwayListener, Divider, useTheme } from "@mui/material";
import { StyledPopover, StyledPopoverProps } from "./styled-popover";
import { DisabledOverlay } from "./disabled-overlay";
import { SimpleTooltip } from "./tooltips";
import { PermissionsToolTipComponent } from "./permissions-tooltip";

/** How long do we need to hover over a context menu button before it open or how long do we have to leave the context menu before it shuts */
const HOVER_DELAY_MS = 200;

export interface ContextMenuAction<TContextMenuActionId = unknown> {
    contextMenuActionId: TContextMenuActionId;
    /** The name displayed for this context menu option */
    displayName: string;
    /** Called when this option is clicked, is also called if this is a submenu button */
    onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    /** Disables this button */
    disabled?: boolean;
    /** If it is a submenu, these are the children for the submenu */
    childActions?: ContextMenuAction<TContextMenuActionId>[];
    /** What icon should be displayed */
    icon?: IconProp;
    /** Adds a divider below this button */
    addDivider?: boolean;
    /** Changes the colour of the button's text and icon if one is provided */
    colour?: string;
    /** Id for testing purposes */
    testId?: string;
    /** Message to show when disabled, default is permissions message */
    DisabledMessage?: () => React.JSX.Element;
}

export interface ContextMenuProps<TContextMenuActionId = unknown> extends StyledPopoverProps {
    /** Actions inside this context menu */
    actions: ContextMenuAction<TContextMenuActionId>[];
    /** Id for the context menu */
    testId?: string;
}

/**
 * Creates a context menu, requires an array of ContextMenuActions for the context menu to generate off.
 */
export const ContextMenu: React.FunctionComponent<ContextMenuProps> = ({ actions, ...popoverProps }) => {
    // Store a ref to the first child so we can automatically focus on it when the context menu is focused
    const firstChildRef = useRef<HTMLElement>(null);

    const buttons = useMemo(
        () =>
            actions.map((action, index) =>
                // eslint-disable-next-line react/jsx-props-no-spreading
                index === 0 ? <ContextMenuButtonForwardRef {...action} ref={firstChildRef} key={action.displayName} /> : <ContextMenuButton {...action} key={action.displayName} />
            ),
        [actions]
    );
    return (
        <ClickAwayListener
            onClickAway={(e) => {
                popoverProps.onClose(e, "backdropClick");
            }}
        >
            <ContextMenuPopover
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...popoverProps}
                disableEnforceFocus
                className="disable-mouse-events"
                classes={{ paper: "enable-mouse-events" }}
                style={{ pointerEvents: "none" }}
                slotProps={{
                    paper: {
                        "data-testid": popoverProps.testId,
                        ...popoverProps.slotProps?.paper,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } as any,
                }}
                onFocus={(e) => {
                    // When focusing on the modal, shift focus to the first child
                    e.stopPropagation();
                    firstChildRef.current?.focus();
                }}
                $withIcons
            >
                {buttons}
            </ContextMenuPopover>
        </ClickAwayListener>
    );
};

const ContextMenuButtonForwardRef = forwardRef<HTMLElement, ContextMenuAction>((props, ref) => ContextMenuButton(props, { ref }));

/**
 * Internal useage to generate a context menu button, checks if the action has child actions and generates the required component
 */
const ContextMenuButton: React.FunctionComponent<ContextMenuAction> = (
    { displayName, onClick, disabled, childActions, icon, colour, addDivider, DisabledMessage = PermissionsToolTipComponent, testId },
    { ref }
) => {
    if (childActions != null && childActions.length > 0) {
        return <SubMenuButton displayName={displayName} onClick={onClick} disabled={disabled} childActions={childActions} icon={icon} addDivider={addDivider} testId={testId} />;
    }
    return (
        <div style={{ position: "relative" }}>
            <SimpleTooltip disabled={!disabled} text={<DisabledMessage />} placement="right">
                <span>
                    {disabled && <DisabledOverlay />}
                    <StyledContextMenuInteractiveElement
                        ref={ref}
                        data-testid={testId}
                        className="button"
                        onClick={(e) => {
                            // Firefox disabled overlay does not work
                            // This can be removed when is complete: https://dev.azure.com/iventis/Iventis/_workitems/edit/16665
                            if (!disabled) {
                                e.stopPropagation();
                                onClick?.(e);
                            }
                        }}
                        // Stop propagation on focus to prevent on focus being called on the sub menu, we only want focus called on the button
                        onFocus={(e) => e.stopPropagation()}
                        style={{ color: colour }}
                    >
                        {icon && <FontAwesomeIcon icon={icon} color={colour} />}

                        <span>{displayName}</span>
                    </StyledContextMenuInteractiveElement>
                </span>
            </SimpleTooltip>
            {addDivider && <Divider />}
        </div>
    );
};

/**
 * Creates a submenu when the button is clicked or hovered
 */
const SubMenuButton: React.FunctionComponent<Omit<ContextMenuAction, "contextMenuActionId">> = ({
    displayName,
    onClick,
    disabled,
    childActions,
    icon,
    colour,
    addDivider,
    testId,
}) => {
    // True if the button was clicked
    const [opened, setOpened] = useState(false);

    // True when mouse is over the button
    const [hoverOpened, setHoverOpened] = useState(false);

    // Store a reference to our delayedHandlers
    const closeDelayHandler = useRef<NodeJS.Timeout>(null);

    const handleMouseOver = () => {
        clearTimeout(closeDelayHandler.current);
        setHoverOpened(true);
    };

    const handleMouseOut = () => {
        closeDelayHandler.current = setTimeout(() => {
            setHoverOpened(false);
        }, HOVER_DELAY_MS);
    };

    // Clear timeouts on unmounting this component
    useEffect(
        () => () => {
            setHoverOpened(false);
            clearTimeout(closeDelayHandler.current);
        },
        []
    );

    // Store a reference to the button to anchor the sub menu
    const buttonRef = useRef<HTMLButtonElement>();
    const theme = useTheme<Theme>();
    return (
        <>
            <div>
                <span style={{ position: "relative" }}>
                    {/* Use disabled overlay so we can still register mouse events on the button */}
                    {disabled && <DisabledOverlay />}
                    <StyledContextMenuInteractiveElement
                        className="button"
                        data-testid={testId}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Make sure we're not disabled as onClick can still be called if the button is focused and enter is pressed
                            if (!disabled) {
                                setOpened(!opened);
                                onClick?.(e);
                            }
                        }}
                        ref={buttonRef}
                        onMouseOver={handleMouseOver}
                        onMouseOut={handleMouseOut}
                        // Stop propagation on focus to prevent on focus being called on the sub menu, we only want focus called on the button
                        onFocus={(e) => e.stopPropagation()}
                        style={{ color: colour }}
                    >
                        {icon && <FontAwesomeIcon icon={icon} color={colour} />}
                        <TextAndChevronContainer>
                            {displayName}
                            <FontAwesomeIcon icon={["fas", "chevron-right"]} style={{ padding: "15px" }} color={theme.shades.two} />
                        </TextAndChevronContainer>
                    </StyledContextMenuInteractiveElement>
                </span>
                {addDivider && <Divider />}
            </div>
            <ContextMenu
                actions={childActions}
                // Open the sub menu if we have clicked the button or if we are hovered over the button
                open={opened || hoverOpened}
                space={44}
                onClose={() => setOpened(false)}
                $withIcons
                anchorEl={buttonRef.current}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: contextMenuRules.anchorVerticalOffset,
                    horizontal: contextMenuRules.anchorHorizontalOffset,
                }}
                slotProps={{
                    paper: {
                        onMouseOver: handleMouseOver,
                        onMouseOut: handleMouseOut,
                    },
                }}
            />
        </>
    );
};
const TextAndChevronContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${inlineTextIconMargin};
`;

const ContextMenuPopover = styled(StyledPopover)`
    // Disable mouse events on the MuiPopper but enable them on the paper, ensure click events can still occur outside the context menu
    .enable-mouse-events {
        pointer-events: auto;
    }

    .disable-mouse-events {
        pointer-events: none;
    }

    .MuiPopover-paper {
        border-radius: ${contextMenuRules.borderRadius};
        padding: ${contextMenuRules.padding};
        min-width: 220px;
    }
`;

const StyledContextMenuInteractiveElement = styled(InteractiveElement)`
    border-radius: ${contextMenuRules.borderRadius};
    position: relative;

    :hover {
        background-color: ${({ theme }: { theme: Theme }) => theme.shades.lightBorder};
    }
`;
